import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { Code, Copy, Loader2, Send, Share2 } from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { APP_WRITE_RELAY_URLS } from '@/lib/appRelays';
import { buildEmbedSnippet, buildEmbedUrl } from '@/lib/embed';
import { getDisplayName } from '@/lib/getDisplayName';
import { highlightPath, type Highlight } from '@/lib/highlights';
import { nip19 } from 'nostr-tools';

/** Max excerpt length when quoting a highlight inside a kind 1 note. */
const MAX_NOTE_QUOTE = 400;

function excerpt(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= MAX_NOTE_QUOTE) return collapsed;
  return collapsed.slice(0, MAX_NOTE_QUOTE - 1).trimEnd() + '…';
}

interface ShareMenuProps {
  highlight: Highlight;
}

/** Share actions for a highlight: copy link, share to Nostr, embed on the web. */
export function ShareMenu({ highlight }: ShareMenuProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const author = useAuthor(highlight.event.pubkey);

  const [nostrDialogOpen, setNostrDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [comment, setComment] = useState('');

  const permalink = `${window.location.origin}${highlightPath(highlight)}`;
  const nevent = nip19.neventEncode({ id: highlight.event.id, author: highlight.event.pubkey });
  const embedUrl = buildEmbedUrl(highlight, getDisplayName(author.data?.metadata, highlight.event.pubkey));
  const embedSnippet = buildEmbedSnippet(embedUrl, highlight);

  // Rebroadcast the (already signed) highlight to glowmark's relays so
  // embeds and permalinks hydrate for anonymous viewers, even when the
  // author's own write relays don't overlap with our read set.
  const ensureShared = () => {
    nostr.group(APP_WRITE_RELAY_URLS).event(highlight.event).catch(() => {
      // Best-effort availability boost — ignore failures silently.
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(permalink);
      toast({ title: 'Link copied', description: 'Permalink to this highlight is in your clipboard.' });
    } catch {
      toast({ title: 'Permalink', description: permalink });
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      toast({ title: 'Embed code copied', description: 'Paste it into any blog or page that allows iframes.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select and copy the code manually.', variant: 'destructive' });
    }
  };

  const shareToNostr = async () => {
    const parts: string[] = [];
    if (comment.trim()) parts.push(comment.trim());
    if (highlight.quote) parts.push(`“${excerpt(highlight.quote)}”`);
    parts.push(`nostr:${nevent}`);
    parts.push(permalink);

    try {
      await createEvent({
        kind: 1,
        content: parts.join('\n\n'),
        tags: [['q', highlight.event.id, '', highlight.event.pubkey]],
      });
      toast({ title: 'Shared to Nostr', description: 'Your note is live on your relays.' });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'engagement'] });
      setNostrDialogOpen(false);
      setComment('');
    } catch {
      toast({
        title: 'Failed to share',
        description: 'Your relays may be unreachable. Try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <DropdownMenu onOpenChange={(open) => open && ensureShared()}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Share"
            className="flex items-center gap-1.5 h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-transparent"
          >
            <Share2 className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={copyLink}>
            <Copy className="size-4" />
            Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setNostrDialogOpen(true)}>
            <Send className="size-4" />
            Share to Nostr
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setEmbedDialogOpen(true)}>
            <Code className="size-4" />
            Embed on the web
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share to Nostr */}
      <Dialog open={nostrDialogOpen} onOpenChange={setNostrDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share to Nostr</DialogTitle>
            <DialogDescription>
              Posts a note quoting this highlight, with a link back to glowmark.
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="py-4 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-muted-foreground max-w-xs">
                Log in with your Nostr key to share this highlight.
              </p>
              <LoginArea className="max-w-44" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="share-comment">
                  Comment <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="share-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Why is this passage worth reading?"
                  rows={2}
                />
              </div>

              {highlight.quote && (
                <blockquote className="border-l-4 border-primary/70 pl-4 py-1 font-serif text-sm leading-relaxed max-h-32 overflow-y-auto text-muted-foreground">
                  “{excerpt(highlight.quote)}”
                </blockquote>
              )}
            </div>
          )}

          {user && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setNostrDialogOpen(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button onClick={shareToNostr} disabled={isPending}>
                {isPending && <Loader2 className="size-4 animate-spin" />}
                Share
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Embed code */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Embed this highlight</DialogTitle>
            <DialogDescription>
              Paste into any page that allows iframes (on Ghost: an HTML card). The embed shows
              the quote, credit, and live zap count — readers can zap right from your page. Where
              scripts are allowed it auto-sizes to its content; otherwise it stays compact.
            </DialogDescription>
          </DialogHeader>

          <pre className="text-xs bg-muted border border-border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all select-all">
            {embedSnippet}
          </pre>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmbedDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={copyEmbed}>
              <Copy className="size-4" />
              Copy embed code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
